import { AddressZero } from "@ethersproject/constants";

import {
  Decimal18,
  AstridDaoStoreState,
  AstridDaoStoreBaseState,
  VaultWithPendingRedistribution,
  StabilityDeposit,
  AtidStake,
  AstridDaoStore,
  Fees,
  CollateralDecimal
} from "@astrid-dao/lib-base";

import { decimalify18, promiseAllValues } from "./_utils";
import { ReadableEthersAstridDao } from "./ReadableEthersAstridDao";
import { EthersAstridDaoConnection, _getProvider } from "./EthersAstridDaoConnection";
import { EthersCallOverrides, EthersProvider } from "./types";

/**
 * Extra state added to {@link @astrid-dao/lib-base#AstridDaoStoreState} by
 * {@link BlockPolledAstridDaoStore}.
 *
 * @public
 */
export interface BlockPolledAstridDaoStoreExtraState {
  /**
   * Number of block that the store state was fetched from.
   *
   * @remarks
   * May be undefined when the store state is fetched for the first time.
   */
  blockTag?: number;

  /**
   * Timestamp of latest block (number of seconds since epoch).
   */
  blockTimestamp: number;

  /** @internal */
  _feesFactory: (blockTimestamp: number, recoveryMode: boolean) => Fees;
}

/**
 * The type of {@link BlockPolledAstridDaoStore}'s
 * {@link @astrid-dao/lib-base#AstridDaoStore.state | state}.
 *
 * @public
 */
export type BlockPolledAstridDaoStoreState =
  AstridDaoStoreState<BlockPolledAstridDaoStoreExtraState>;

/**
 * Ethers-based {@link @astrid-dao/lib-base#AstridDaoStore} that updates state whenever there's a new
 * block.
 *
 * @public
 */
export class BlockPolledAstridDaoStore extends AstridDaoStore<BlockPolledAstridDaoStoreExtraState> {
  readonly connection: EthersAstridDaoConnection;

  private readonly _readable: ReadableEthersAstridDao;
  private readonly _provider: EthersProvider;

  constructor(readable: ReadableEthersAstridDao) {
    super();

    this.connection = readable.connection;
    this._readable = readable;
    this._provider = _getProvider(readable.connection);
  }

  private async _getRiskiestVaultBeforeRedistribution(
    overrides?: EthersCallOverrides
  ): Promise<VaultWithPendingRedistribution> {
    const riskiestVaults = await this._readable.getVaults(
      { first: 1, sortedBy: "ascendingCollateralRatio", beforeRedistribution: true },
      overrides
    );

    if (riskiestVaults.length === 0) {
      return new VaultWithPendingRedistribution(AddressZero, "nonExistent");
    }

    return riskiestVaults[0];
  }

  private async _get(
    blockTag?: number
  ): Promise<[baseState: AstridDaoStoreBaseState, extraState: BlockPolledAstridDaoStoreExtraState]> {
    const { userAddress } = this.connection;

    const { blockTimestamp, _feesFactory, ...baseState } = await promiseAllValues({
      blockTimestamp: this._readable._getBlockTimestamp(blockTag),
      _feesFactory: this._readable._getFeesFactory({ blockTag }),
      price: this._readable.getPrice({ blockTag }),
      numberOfVaults: this._readable.getNumberOfVaults({ blockTag }),
      totalRedistributed: this._readable.getTotalRedistributed({ blockTag }),
      total: this._readable.getTotal({ blockTag }),
      baiInStabilityPool: this._readable.getBaiInStabilityPool({ blockTag }),
      totalWeightedStakedAtid: this._readable.getTotalWeightedStakedAtid({ blockTag }),
      totalUnweightedStakedAtid: this._readable.getTotalUnweightedStakedAtid({ blockTag }),
      _riskiestVaultBeforeRedistribution: this._getRiskiestVaultBeforeRedistribution({ blockTag }),
      remainingStabilityPoolAtidReward: this._readable.getRemainingStabilityPoolAtidReward({
        blockTag
      }),
      ...(userAddress
        ? {
            accountBalance: this._provider.getBalance(userAddress, blockTag).then(decimalify18),
            baiBalance: this._readable.getBaiBalance(userAddress, { blockTag }),
            xcBaiBalance: this._readable.getXcBaiBalance(userAddress, { blockTag }),
            xcBaiAllowance: this._readable.getXcBaiAllowance(userAddress, { blockTag }),
            baiAllowance: this._readable.getBaiAllowance(userAddress, { blockTag }),
            atidBalance: this._readable.getAtidBalance(userAddress, { blockTag }),
            wrappedTokenBalance: this._readable.getWrappedTokenBalance(userAddress, { blockTag }),
            wrappedTokenAllowance: this._readable.getWrappedTokenAllowance(userAddress, {
              blockTag
            }),
            collateralSurplusBalance: this._readable.getCollateralSurplusBalance(userAddress, {
              blockTag
            }),
            vaultBeforeRedistribution: this._readable.getVaultBeforeRedistribution(userAddress, {
              blockTag
            }),
            stabilityDeposit: this._readable.getStabilityDeposit(userAddress, { blockTag }),
            atidStake: this._readable.getAtidStake(userAddress, { blockTag }),
            totalLockedStakesCount: this._readable.getTotalLockedStakesCount(userAddress, {
              blockTag
            })
          }
        : {
            accountBalance: Decimal18.ZERO,
            baiBalance: Decimal18.ZERO,
            xcBaiBalance: Decimal18.ZERO,
            atidBalance: Decimal18.ZERO,
            wrappedTokenBalance: CollateralDecimal.ZERO,
            wrappedTokenAllowance: CollateralDecimal.ZERO,
            xcBaiAllowance: CollateralDecimal.ZERO,
            baiAllowance: CollateralDecimal.ZERO,
            collateralSurplusBalance: CollateralDecimal.ZERO,
            vaultBeforeRedistribution: new VaultWithPendingRedistribution(
              AddressZero,
              "nonExistent"
            ),
            stabilityDeposit: new StabilityDeposit(
              Decimal18.ZERO,
              Decimal18.ZERO,
              CollateralDecimal.ZERO,
              Decimal18.ZERO
            ),
            atidStake: new AtidStake(),
            totalLockedStakesCount: 0
          })
    });

    return [
      {
        ...baseState,
        _feesInNormalMode: _feesFactory(blockTimestamp, false)
      },
      {
        blockTag,
        blockTimestamp,
        _feesFactory
      }
    ];
  }

  /** @internal @override */
  protected _doStart(): () => void {
    this._get().then(state => {
      if (!this._loaded) {
        this._load(...state);
      }
    });

    const blockListener = async (blockTag: number) => {
      const state = await this._get(blockTag);

      if (this._loaded) {
        this._update(...state);
      } else {
        this._load(...state);
      }
    };

    this._provider.on("block", blockListener);

    return () => {
      this._provider.off("block", blockListener);
    };
  }

  /** @internal @override */
  protected _reduceExtra(
    oldState: BlockPolledAstridDaoStoreExtraState,
    stateUpdate: Partial<BlockPolledAstridDaoStoreExtraState>
  ): BlockPolledAstridDaoStoreExtraState {
    return {
      blockTag: stateUpdate.blockTag ?? oldState.blockTag,
      blockTimestamp: stateUpdate.blockTimestamp ?? oldState.blockTimestamp,
      _feesFactory: stateUpdate._feesFactory ?? oldState._feesFactory
    };
  }
}
